/**
 * EEOC Scenario Helper
 * AI-powered guidance for specific interview scenarios
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Shield, ArrowLeft, HelpCircle, Send, Lightbulb, Loader2,
  MessageSquare, BookOpen, AlertTriangle
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Example scenarios
const EXAMPLE_SCENARIOS = [
  "A candidate mentioned she's pregnant during the interview",
  "The applicant has a strong foreign accent and I'm curious about their background",
  "Someone told me they need to leave early on Fridays for religious reasons",
  "A candidate asked if the role requires a lot of physical activity",
  "The interviewee mentioned they graduated in 1985"
];

export default function EEOCScenarioHelper() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  
  const [scenario, setScenario] = useState('');
  const [loading, setLoading] = useState(false);
  const [responses, setResponses] = useState([]);

  const handleSubmit = async () => {
    if (!scenario.trim()) {
      toast.error('Please describe a scenario');
      return;
    }

    setLoading(true);
    try {
      // Get tenant_id - could be from URL context or auth
      const tenantId = tenant?.id || 'default';
      
      const res = await api.post(`/eeoc/scenarios/ask?tenant_id=${tenantId}`, {
        scenario: scenario,
        user_name: user?.name || 'Anonymous',
        user_email: user?.email || '',
        link_id: linkId
      });

      setResponses([res.data, ...responses]);
      setScenario('');
      toast.success('Guidance received');
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to get guidance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example) => {
    setScenario(example);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="eeoc-scenario-helper">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(`/eeoc/test/${linkId}`)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="w-10 h-10 bg-emerald-600 rounded-md flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Scenario Helper</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">Get AI-powered guidance for tricky situations</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Info Card */}
        <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/20">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                  How can I help?
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Describe any interview scenario you're unsure about. I'll provide guidance on 
                  how to handle it in an EEOC-compliant way, including what to say, what to avoid, 
                  and the legal reasoning behind the recommendation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Input Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Describe Your Scenario</CardTitle>
            <CardDescription>
              What situation are you facing or anticipating in an interview?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              placeholder="Example: A candidate mentioned they need time off for religious holidays..."
              className="min-h-[120px] resize-none"
              data-testid="scenario-input"
            />
            
            {/* Example Scenarios */}
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Try an example:</p>
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_SCENARIOS.map((ex, idx) => (
                  <Badge 
                    key={idx}
                    variant="outline" 
                    className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                    onClick={() => handleExampleClick(ex)}
                  >
                    {ex.substring(0, 40)}...
                  </Badge>
                ))}
              </div>
            </div>
            
            <Button 
              onClick={handleSubmit}
              disabled={loading || !scenario.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-scenario-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Getting Guidance...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Get Guidance
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Responses */}
        {responses.length > 0 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Previous Queries
            </h3>
            
            <ScrollArea className="h-[500px]">
              <div className="space-y-4 pr-4">
                {responses.map((resp, idx) => (
                  <Card key={resp.id || idx}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <Badge variant="outline" className="text-xs mb-2">
                            {resp.category || 'General'}
                          </Badge>
                          <CardTitle className="text-base">{resp.scenario}</CardTitle>
                        </div>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {new Date(resp.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="prose prose-sm prose-slate dark:prose-invert max-w-none">
                        <div className="whitespace-pre-line text-sm text-slate-600 dark:text-slate-400">
                          {resp.guidance}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Empty State */}
        {responses.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <MessageSquare className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 dark:text-white mb-2">No queries yet</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Describe a scenario above to get instant EEOC compliance guidance.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Links */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
          <Button
            variant="outline"
            onClick={() => navigate(`/eeoc/test/${linkId}/guide`)}
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Read EEOC Guide
          </Button>
          <Button
            onClick={() => navigate(`/eeoc/test/${linkId}`)}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Assessment
          </Button>
        </div>
      </main>
    </div>
  );
}
